import { GRSFacet } from 'src/services/search/services/GRS.types';

import { SHOP_BY_PREFIX } from '@/components/Analytics/constants';
import { getGRSFacetsFromConfig } from '@/services/search/services/Grs';
import { isBDSite } from '@/services/search/utils';
import type { GRSFacetConfigProperties } from '@/src/types/contentStack';
import type {
    ComposableWindowArray,
    ContentStackEntryDataProps,
    LocaleProps,
} from '@nxt-costco-com/forge-digital-components';
import {
    getComposableTextByDevFieldKey,
    getFormattedPrice,
} from '@nxt-costco-com/forge-digital-components';

import {
    GRS_CATEGORY_FACET_KEY,
    GRS_RATING_FACET_KEY,
    GRS_SHOW_OUT_OF_STOCK_FACET_KEY,
} from './constants';
import {
    GRS_PRICE_LABEL_KEY,
    GRS_PROGRAM_TYPES_FACET_KEY,
} from './grs.constants';
import type { FacetNormalized } from './types';

function normalizeGRSPriceFacetKey(facetKey: string): string {
    // Strip province/state code from CA price facets: "inventory(1668_ON, price)" → "inventory(1668, price)"
    return facetKey.replace(/^(inventory\(\d{1,4})_[^,]+(, price\))$/, '$1$2');
}

export function isGRSPriceFacetKey(facetKey: string): boolean {
    const normalizedFacetKey = normalizeGRSPriceFacetKey(facetKey);
    return /^inventory\(\d{1,4}, price\)$/.test(normalizedFacetKey);
}

export const normalizeGRSFacets = (
    grsFacets: GRSFacet[],
    grsFacetConfig: ContentStackEntryDataProps<GRSFacetConfigProperties>,
    isFacetFsaChdi: boolean | undefined,
    locale: LocaleProps,
): FacetNormalized[] => {
    const normalizedFacets: FacetNormalized[] = [];
    const grsFacetsFromConfig = getGRSFacetsFromConfig(grsFacetConfig); // hack introduced to get around contentstack limitation on key names with dots
    grsFacets?.forEach(facet => {
        const facetKey = normalizeGRSPriceFacetKey(facet.key);
        let facetConfig =
            grsFacetsFromConfig[facetKey] ?? grsFacetsFromConfig[facet.key];

        // The warehouse ID in the API response for BD sites is dynamic (e.g. "inventory(1668_ON, price)") and may
        // differ from the static warehouse ID in the CS config (e.g. "inventory(889, price)").
        // Fall back to the first price facet config entry found in CS config.
        if (!facetConfig && isGRSPriceFacetKey(facet.key)) {
            const priceConfigKey = Object.keys(grsFacetsFromConfig).find(key =>
                isGRSPriceFacetKey(key),
            );
            if (priceConfigKey)
                facetConfig = grsFacetsFromConfig[priceConfigKey];
        }

        if (!facetConfig) {
            return;
            // Rating facet
        } else if (facetKey === GRS_RATING_FACET_KEY) {
            normalizedFacets.push({
                facetKey,
                label: facetConfig?.label ?? '',
                multiSelect: facet.dynamicFacet,
                queryParamKey: facetConfig?.queryParamKey ?? facetKey,
                buckets: facet.values.map(value => {
                    return {
                        low: value.interval?.minimum ?? value.minValue,
                        high: value.interval?.maximum ?? value.maxValue,
                        count: value.count,
                        val: value.interval?.minimum ?? value.minValue,
                        // adding this field in facets buckets to match the similar structure as LucidWorks for GRS
                        leads_to_fsa_chdi: isFacetFsaChdi,
                    };
                }),
            });
            // Warehouse Price facet
        } else if (isGRSPriceFacetKey(facet.key)) {
            normalizedFacets.push({
                facetKey,
                label: facetConfig?.label ?? '',
                multiSelect: facet.dynamicFacet,
                queryParamKey: facetConfig?.queryParamKey ?? facetKey,
                buckets: facet.values.map(value => {
                    return {
                        low: value.interval?.minimum ?? value.minValue,
                        high: value.interval?.maximum ?? value.maxValue,
                        count: value.count,
                        val:
                            value.interval?.minimum !== undefined &&
                            value.interval?.maximum !== undefined
                                ? getComposableTextByDevFieldKey({
                                      devFieldKey: GRS_PRICE_LABEL_KEY,
                                      composableWindow:
                                          grsFacetConfig.composable_window as ComposableWindowArray,
                                      templateVariables: {
                                          priceMinimum: getFormattedPrice(
                                              locale,
                                              value.interval.minimum,
                                              true,
                                          ),
                                          priceMaximum: getFormattedPrice(
                                              locale,
                                              value.interval.maximum,
                                              true,
                                          ),
                                      },
                                  })
                                : '',
                        leads_to_fsa_chdi: isFacetFsaChdi,
                    };
                }),
            });
        } else if (facetKey === GRS_CATEGORY_FACET_KEY) {
            normalizedFacets.push({
                facetKey,
                label: facetConfig?.label ?? '',
                multiSelect: facet.dynamicFacet,
                queryParamKey: facetConfig?.queryParamKey ?? facetKey,
                buckets: facet.values.map(value => {
                    const cateogryAttributes = getCategoryAttributes(
                        value.value ?? '',
                    );
                    return {
                        low: value.minValue,
                        high: value.maxValue,
                        count: value.count,
                        val: value.value ?? '',
                        meta: cateogryAttributes?.url,
                        leads_to_fsa_chdi: isFacetFsaChdi,
                    };
                }),
            });
        } else {
            normalizedFacets.push({
                facetKey,
                label: facetConfig?.label ?? '',
                multiSelect: facet.dynamicFacet,
                queryParamKey: facetConfig?.queryParamKey ?? facetKey,
                buckets: facet.values.map(value => {
                    return {
                        low: value.minValue,
                        high: value.maxValue,
                        count: value.count,
                        val: value.value ?? '',
                        leads_to_fsa_chdi: isFacetFsaChdi,
                    };
                }),
            });
        }
    });

    // Always present for non-BD sites, no facet returned by GRS to indicate if out of stock items should be shown
    if (!isBDSite(process.env.NEXT_PUBLIC_SITE))
        normalizedFacets.push({
            facetKey: GRS_SHOW_OUT_OF_STOCK_FACET_KEY,
            label:
                grsFacetsFromConfig[GRS_SHOW_OUT_OF_STOCK_FACET_KEY]?.label ??
                '',
            multiSelect: false,
            queryParamKey:
                grsFacetsFromConfig[GRS_SHOW_OUT_OF_STOCK_FACET_KEY]
                    ?.queryParamKey ?? GRS_SHOW_OUT_OF_STOCK_FACET_KEY,
            buckets: [
                {
                    count: 1,
                    val: '1',
                    low: 0,
                    high: 0,
                    leads_to_fsa_chdi: isFacetFsaChdi,
                },
            ],
        });

    return normalizedFacets;
};

export const getFilterType = (facetKey: string) => {
    let filterType;
    const normalizedFacetKey = normalizeGRSPriceFacetKey(facetKey);

    if (normalizedFacetKey === GRS_RATING_FACET_KEY) {
        filterType = 'Customer Reviews';
    } else if (isGRSPriceFacetKey(normalizedFacetKey)) {
        filterType = 'Price';
    } else if (normalizedFacetKey === GRS_PROGRAM_TYPES_FACET_KEY) {
        filterType = 'Location';
    } else if (normalizedFacetKey.includes('attributes.')) {
        filterType = normalizedFacetKey.split('attributes.')[1];
    } else {
        filterType = normalizedFacetKey;
    }

    return filterType ? SHOP_BY_PREFIX + filterType.replace(/_/g, ' ') : '';
};

export const getCategoryAttributes = (
    categoryInfo: string,
): {
    label: string | undefined;
    url: string | undefined;
    id: string | undefined;
    level: number | undefined;
    parent: string | undefined | null;
    l1cateogry: string | undefined | null;
    l2cateogry: string | undefined | null;
} => {
    const parts = categoryInfo?.split('_');
    const id = parts?.[0];
    const label = parts?.[1];
    const hierarchy = parts?.[2];
    const url = parts?.[3] && `${parts[3]}.html`;
    const hierarchyArr = hierarchy?.split('|');

    const level =
        !hierarchyArr?.length || hierarchyArr?.length === 0
            ? 1
            : hierarchyArr?.length;

    const parent =
        hierarchyArr?.length && hierarchyArr.length > 1
            ? hierarchyArr?.[hierarchyArr.length - 2]
            : null;

    const l1cateogry =
        hierarchyArr?.length && hierarchyArr.length > 0
            ? hierarchyArr?.[0]
            : null;

    const l2cateogry =
        hierarchyArr?.length && hierarchyArr.length > 1
            ? hierarchyArr?.[1]
            : null;

    return {
        label,
        url,
        id,
        level,
        parent,
        l1cateogry,
        l2cateogry,
    };
};
